/**
 * Bonndle API Client
 * Base client for communicating with Boldscale backend services
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://boldscaleservices.com';

// Storage keys for auth persistence
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'bonndle:access_token',
  USER_ID: 'bonndle:user_id',
  USER_DATA: 'bonndle:user_data',
  PROFILE_COMPLETE: 'bonndle:profile_complete',
};

/**
 * Get stored auth token
 */
export function getAuthToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Set auth token in storage
 */
export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }
}

/**
 * Get stored user data
 */
export function getStoredUser() {
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (!token) return null;

  return {
    id: userId,
    token,
    ...(userData ? JSON.parse(userData) : {}),
  };
}

/**
 * Store user data
 */
export function storeUserData(user) {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    // API returns user_uuid, not id or uuid
    const userId = user.user_uuid || user.id || user.uuid;

    if (userId) {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
      console.log('[storeUserData] Stored user ID:', userId);
    } else {
      console.warn('[storeUserData] No user ID found in user object. User keys:', Object.keys(user));
    }
  }
}

/**
 * Clear all auth data from storage
 */
export function clearAuth() {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getAuthToken();
}

/**
 * Refresh the access token
 * @returns {Promise<boolean>} True if refresh succeeded
 */
export async function refreshToken() {
  const token = getAuthToken();
  if (!token) return false;

  try {
    const response = await fetch(`${API_URL}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.access_token) {
        setAuthToken(data.access_token);
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

/**
 * Base request handler with authentication
 * @param {string} endpoint - API endpoint (e.g., '/users/me')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export async function request(endpoint, options = {}) {
  const token = getAuthToken();

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_URL}${endpoint}`, config);

  // Handle token expiration (401)
  if (response.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      // Retry original request with new token
      const newToken = getAuthToken();
      config.headers.Authorization = `Bearer ${newToken}`;
      const retryResponse = await fetch(`${API_URL}${endpoint}`, config);

      if (!retryResponse.ok) {
        const error = await retryResponse.json().catch(() => ({}));
        throw new Error(error.message || error.detail || `Request failed: ${retryResponse.status}`);
      }

      return retryResponse.json();
    }

    // Refresh failed - clear auth
    clearAuth();
    window.dispatchEvent(new CustomEvent('bonndle:auth:expired'));
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.detail || `Request failed: ${response.status}`);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return { success: true };
}

/**
 * Safe request handler - doesn't trigger logout on 401
 * Use this for non-critical data fetching that shouldn't log out the user on failure
 * @param {string} endpoint - API endpoint
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export async function safeRequest(endpoint, options = {}) {
  const token = getAuthToken();

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_URL}${endpoint}`, config);

  // On 401, just throw without clearing auth or dispatching events
  if (response.status === 401) {
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.detail || `Request failed: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return { success: true };
}

/**
 * Make unauthenticated request (for login/register)
 * @param {string} endpoint - API endpoint
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export async function publicRequest(endpoint, options = {}) {
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(`${API_URL}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.detail || `Request failed: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return { success: true };
}

/**
 * Get the API base URL
 */
export function getApiUrl() {
  return API_URL;
}

export default {
  request,
  publicRequest,
  getAuthToken,
  setAuthToken,
  getStoredUser,
  storeUserData,
  clearAuth,
  isAuthenticated,
  refreshToken,
  getApiUrl,
  STORAGE_KEYS,
};
